import { Pathname } from 'history';
import { CookieAttributes } from 'js-cookie';
import { To, RelativeRoutingType } from 'react-router-dom';

import { AccountsBuyResponse } from '@modules/accounts/types';
import { Redirect3DSResponse } from '@modules/cards/types';
import {
  Currencies,
  CryptoCurrencyEnum,
  CurrencyEnum,
  FiatCurrencyEnum,
} from '@modules/global/types';
import { LoginStateType } from '@modules/login/state';
import { AuthFlowEnum, VerifyNextEnum } from '@modules/types';
import { BillingAddressParams } from '@modules/user/types';
import { GETWithdrawRateResponse } from '@modules/withdraw/types';

import { RateType } from '@containers/Converter/useConverterState';

import { LanguageType } from './i18n';

export enum EnvsEnum {
  local = 'local',
  prod = 'prod',
  test = 'test',
}

export enum SchemesEnum {
  dark = 'dark',
  light = 'light',
}

export enum ThemesEnum {
  oneInch = '1inch',
  default = 'default',
}

export enum PaymentSystemsEnum {
  mastercard = 'mastercard',
  mastercarddebit = 'mastercarddebit',
  visa = 'visa',
  union_pay = 'union_pay',
  rupay = 'rupay',
}

export enum CookieKeys {
  referral_code = 'referral_code',
  mercuryoCookiesAccepted = 'mercuryoCookiesAccepted',
  token = 'token',
  idleTimeout = 'idleTimeout',
}

export enum LocalStorageKeys {
  isStrongModal = 'isStrongModal',
  verification = 'verification',
  idle_timestamp = 'idle_timestamp',
  KYCVideoShown = 'KYCVideoShown',
  KYCStarted = 'KYCStarted',
  scheme = 'scheme',
  i18nextLng = 'i18nextLng',
}

export type LocalStorageType = {
  isStrongModal: boolean;
  verification: LoginStateType['verification'];
  idle_timestamp: number;
  forethought_handoff_state: { unreadMessages: number; expiry: number };
  KYCVideoShown: boolean;
  KYCStarted: boolean;
  scheme: SchemesEnum;
  i18nextLng: LanguageType;
  authFlow: AuthFlowEnum;
  ibanSurvey: string;
};

export type NotificationData = {
  title?: string;
  message: string;
};

export type LocationStateType = {
  login: {
    isResetPassword?: boolean;
    isSetEmail?: boolean;
  };
  account: Record<'password', string>;
  descriptor: {
    id?: string;
    currency?: CurrencyEnum;
    amount?: string;
  };
  rate: Record<'rate', GETWithdrawRateResponse>;
  profileSetPhone?: {
    nextPage?: Pathname;
    strongPassword?: boolean;
    iban?: boolean;
  };
  profileChangePhone?: Record<'strongPassword', boolean>;
  profileSetEmail?: Record<'strongPassword', boolean>;
  profileOldEmail?: Record<'strongPassword', boolean>;
  profileChangePassword?: Record<'strongPassword', boolean>;
  sendNewEmail?: {
    nextPage?: string;
    token?: string;
    strongPassword?: boolean;
  };
  verifyPassword?: {
    nextPage?: string;
    token?: string;
    strongPassword?: boolean;
  };
  sendOldEmail?: {
    nextPage?: string;
    strongPassword?: boolean;
  };
  setCountry: Record<'isCountryPage', boolean>;
  accounts: {
    iban?: boolean;
  };
  verifications: {
    rate?: GETWithdrawRateResponse;
    token?: string;
    nextPage: Pathname;
    password?: string;
    strongPassword?: boolean;
    value?: string;
    sellData?: {
      card_id: string;
      sell_token: string;
    };
    flow:
      | 'changeEmailVerifyPhone'
      | 'changeEmailVerifyOldEmail'
      | 'changeEmailVerifyEmail'
      | 'changePhoneVerifyEmail'
      | 'changePhoneVerifyPhone'
      | 'changePasswordVerifyPhone'
      | 'changePasswordVerifyEmail'
      | 'withdrawVerifyEmail'
      | 'changePasswordVerifyOTP'
      | 'ChangeEmailVerifyOTP'
      | 'ChangePhoneVerifyOTP'
      | 'ToggleStrongSecurityOTP'
      | 'SellVerifyOTP'
      | 'WithdrawVerify2FA';
    iban?: boolean;
  };
  limits: {
    from: string;
    fiatCurrency: CurrencyEnum;
  };
  verification: {
    password: string;
    token?: string;
    strongPassword?: boolean;
  };
  verifyOtp: {
    redirectTo: string;
    id: string;
    cardNumber?: string;
    notificationData?: {
      title: string;
      message: string;
    };
  };
  verifyCard: {
    amount?: string;
    currency: CurrencyEnum;
    redirect?: Redirect3DSResponse;
    buy_token?: string;
    card_id?: string;
    cvv?: string;
  };
  wallet: {
    id?: string;
    currency?: CurrencyEnum;
  };
  addCard: {
    amount?: string;
    currency?: FiatCurrencyEnum;
    showFailedNotification: boolean;
  };
  wallets: {
    cacheConverter: boolean;
    buySuccessNotification?: string | boolean;
    buyErrorNotification?: boolean;
    bindCarsSuccessNotification?: boolean;
    bindCardFailedNotification?: boolean;
  };
  accountsBuyInvoiceDetails: {
    detailsData: {
      rate?: RateType;
      amountSubtotal?: string;
      amountTotal?: string;
      currency?: CurrencyEnum;
      fee?: string;
      feeCurrency?: CurrencyEnum;
      operationType: string;
      currencies: Currencies;
    };
    paymentDetails: AccountsBuyResponse;
  };
  accountsSendVerification: {
    next: VerifyNextEnum.verifyPhone | VerifyNextEnum.verifyEmail;
  };
  verifyIdentity?: {
    from?: string;
    fromLogin?: boolean;
  };
  profileEdit?: {
    from?: string;
  };
  billingAddress?: {
    redirectTo?: string;
    from?: string;
    notificationData?: NotificationData;
    address?: BillingAddressParams;
  };
};

export type ResolveUnstoppableDomainType = (
  domain: string,
  currency: CryptoCurrencyEnum
) => Promise<string>;

export type BemCSSModulesType = (
  style: { [key: string]: string },
  className: string
) => (...args: any) => string;

export type TrimStringMiddleType = (value: string, charCount: number) => string;

export type FingerPrintDataType = () => Promise<{ [key: string]: string }>;

export type IntlDateType = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions,
  language?: string
) => string;

export type TestKeyType = (key?: string) => void;

export type FullNameType = (firstName?: string | null, lastName?: string | null) => string;

export type CookieType<Key = keyof typeof CookieKeys> = {
  setCookie: <U>(
    name: Key,
    value: NonNullable<U>,
    options?: CookieAttributes
  ) => string | undefined;
  getCookie: (name: Key) => ReturnType<typeof JSON.parse>;
  removeCookie: (name: Key, options?: CookieAttributes) => void;
};

export type LocationStateKeys = keyof LocationStateType;
export type LocationStateValues = LocationStateType[LocationStateKeys];

export type NavigateType = <T extends LocationStateKeys>(
  to: To,
  options?: {
    replace?: boolean;
    state?: T extends LocationStateKeys
      ? LocationStateType[T]
      : LocationStateType[LocationStateKeys];
    relative?: RelativeRoutingType;
  }
) => void;

export type LanguageCodeTypes = LanguageType;
export type LanguageCodeValues = (typeof CONST.i18n.languages)[LanguageCodeTypes];
export type LanguageCodeType = LanguageCodeValues['code'];

export type RemoveTrailingZerosType = (value: string | number) => string;
export type CorrectingTrailingNumbersType = (
  value: string | number,
  displayDigits?: number,
  totalDigits?: number
) => string;

export type FooterMenuLink = (typeof CONST.infoLinks)[number];
